// Document → PSD encoder.
//
// Converts a Pixelpa `Document` (layer tree with RasterLayer, GroupLayer,
// TypeLayer, AdjustmentLayer) into a `PsdFile` structure and delegates to
// `writePsd` for the byte-level encoding.
//
// Strategy:
//   1. Flatten the layer tree to a sequential list of PsdLayerRecord entries.
//      PSD stores layers in top-to-bottom order (reverse of our doc.layers).
//   2. Groups are encoded using lsct section-divider sentinels:
//      - Type 1 (open folder) before the group's children
//      - Type 3 (closing sentinel) after the group's children
//   3. Adjustment layers and groups carry no pixel data; we emit a 1×1
//      transparent pixel buffer and set the "pixel data irrelevant" flag.
//   4. Type layers are treated like adjustment layers (no pixel data in PSD
//      without tySh descriptor — we include a luni name block).
//   5. Composite all visible raster layers for the merged image.

import type { Document } from "../../document/Document";
import type {
  Layer,
  RasterLayer,
  GroupLayer,
  AdjustmentLayer,
  BlendMode,
  SmartObjectLayer,
  BackgroundLayer,
  SolidFillLayer,
  GradientFillLayer,
  PatternFillLayer,
  ShapeLayer,
} from "../../document/Layer";
import { isTypeLayer, type TypeLayer } from "../../document/TypeLayer";
import type { PixelBuffer } from "../../pixel/PixelBuffer";
import { writePsd } from "./writePsd";
import type {
  PsdFile,
  PsdLayerRecord,
  PsdChannelInfo,
  PsdAdditionalLayerInfo,
} from "./types";
import type { LayerStyles } from "../../document/LayerStyle";
import {
  buildBaselineImageResources,
  buildDefaultBlendingRanges,
} from "./psdImageResources";

// Blend mode mapping: our kebab-case → PSD 4-char keys.
const BLEND_MODE_KEYS: Record<string, string> = {
  normal: "norm",
  dissolve: "diss",
  darken: "dark",
  multiply: "mul ",
  "color-burn": "idiv",
  "linear-burn": "lbrn",
  "darker-color": "dkCl",
  lighten: "lite",
  screen: "scrn",
  "color-dodge": "div ",
  "linear-dodge": "lddg",
  "lighter-color": "lgCl",
  overlay: "over",
  "soft-light": "sLit",
  "hard-light": "hLit",
  "vivid-light": "vLit",
  "linear-light": "lLit",
  "pin-light": "pLit",
  "hard-mix": "hMix",
  difference: "diff",
  exclusion: "smud",
  subtract: "fsub",
  divide: "fdiv",
  hue: "hue ",
  saturation: "sat ",
  color: "colr",
  luminosity: "lum ",
};

function blendModeToPsd(mode: BlendMode | string): string {
  return BLEND_MODE_KEYS[mode] ?? "norm";
}

/** Encode a Document to a PSD byte stream. */
export async function encodePsd(
  doc: Document,
  opts?: {
    /**
     * Guide list to embed as image resource 1032.  Pass the app's current
     * guides array so they round-trip correctly.  If omitted, no guide
     * resource is written.
     */
    guides?: ReadonlyArray<{ kind: "horizontal" | "vertical"; position: number }>;
    /**
     * ICC profile bytes to preserve as image resource 1039.  If omitted (or
     * empty), no ICC resource is written.  NOTE: the profile is preserved
     * verbatim for round-trip fidelity; it is NOT applied during rendering.
     */
    iccProfile?: Uint8Array;
  },
): Promise<Uint8Array> {
  // Build the flat layer list (top-to-bottom for PSD).
  const { layers: psdLayers, layerPixels } = buildLayerRecords(doc);

  // Build the PsdFile structure.
  // PSD spec: when the layer count is *negative* the first alpha channel of
  // the merged composite is the transparency mask.  For Pixelpa-exported
  // files we always want that — Photoshop opens transparent areas correctly
  // and apps that only read the merged composite (Preview.app, Finder
  // thumbnails) honor the alpha.  Encode the count as a negative number.
  const layerCountRaw = psdLayers.length > 0 ? -psdLayers.length : 0;

  const file: PsdFile = {
    header: {
      version: 1,
      channels: 4, // R, G, B, A
      height: doc.height,
      width: doc.width,
      depth: 8,
      colorMode: 3, // RGB
    },
    colorModeData: new Uint8Array(0),
    imageResources: buildBaselineImageResources({
      width: doc.width,
      height: doc.height,
      dpi: doc.dpi || 72,
      layerCount: psdLayers.length,
      ...(opts?.guides ? { guides: opts.guides } : {}),
      ...(opts?.iccProfile ? { iccProfile: opts.iccProfile } : {}),
    }),
    layerInfo: {
      layerCount: psdLayers.length,
      layerCountRaw,
      layers: psdLayers,
      globalLayerMaskData: new Uint8Array(0),
      additionalInfo: [],
    },
    imageDataOffset: -1,
  };

  // Composite all visible layers for the merged image.
  const mergedComposite = compositeDocument(doc);

  return writePsd(file, layerPixels, mergedComposite);
}

// -- Layer record builder ---------------------------------------------------

interface LayerBuildResult {
  layers: PsdLayerRecord[];
  layerPixels: Map<number, Uint8Array>;
}

function buildLayerRecords(doc: Document): LayerBuildResult {
  const layers: PsdLayerRecord[] = [];
  const layerPixels = new Map<number, Uint8Array>();
  let pixelIndex = 0;

  // doc.layers is bottom-first; PSD wants top-first.
  const topFirst = [...doc.layers].reverse();
  for (const layer of topFirst) {
    emitLayer(
      layer,
      layers,
      layerPixels,
      () => pixelIndex++,
      doc.width,
      doc.height,
    );
  }

  return { layers, layerPixels };
}

function emitLayer(
  layer: Layer,
  out: PsdLayerRecord[],
  pixelMap: Map<number, Uint8Array>,
  nextIndex: () => number,
  docWidth: number,
  docHeight: number,
): void {
  if (layer.kind === "group") {
    emitGroup(layer as GroupLayer, out, pixelMap, nextIndex, docWidth, docHeight);
  } else if (layer.kind === "raster") {
    emitRasterLayer(layer as RasterLayer, out, pixelMap, nextIndex);
  } else if (isTypeLayer(layer)) {
    emitTypeLayer(layer as TypeLayer, out, pixelMap, nextIndex);
  } else if (layer.kind === "adjustment") {
    emitAdjustmentLayer(layer as AdjustmentLayer, out, pixelMap, nextIndex);
  } else if (layer.kind === "smart-object") {
    emitSmartObjectLayer(layer as SmartObjectLayer, out, pixelMap, nextIndex);
  } else if (layer.kind === "background") {
    emitBackgroundLayer(layer as BackgroundLayer, out, pixelMap, nextIndex);
  } else if (
    layer.kind === "solid-fill" ||
    layer.kind === "gradient-fill" ||
    layer.kind === "pattern-fill" ||
    layer.kind === "shape"
  ) {
    // Vector / fill layers: rasterize to the doc canvas and emit as a
    // raster.  This loses the vector/parametric edit data but keeps the
    // pixel appearance round-trip-stable.  See #32: full TySh / GdFl / SoCo
    // descriptor preservation is deferred follow-up work.
    emitRasterizedLayer(layer, out, pixelMap, nextIndex, docWidth, docHeight);
  }
}

function emitGroup(
  group: GroupLayer,
  out: PsdLayerRecord[],
  pixelMap: Map<number, Uint8Array>,
  nextIndex: () => number,
  docWidth: number,
  docHeight: number,
): void {
  // Open folder sentinel (lsct type 1).  The divider record advertises a
  // 1×1 RGBA area so we must also push pixel data for it, otherwise the
  // writer will throw at the !rgba check.
  out.push(buildSectionDivider(group.name, 1, group));
  pixelMap.set(nextIndex(), new Uint8Array([0, 0, 0, 0]));

  // Children (already in top-first order within the group).
  for (const child of group.children) {
    emitLayer(child, out, pixelMap, nextIndex, docWidth, docHeight);
  }

  // Closing sentinel (lsct type 3).
  out.push(buildSectionDivider("", 3, group));
  pixelMap.set(nextIndex(), new Uint8Array([0, 0, 0, 0]));
}

function emitRasterLayer(
  layer: RasterLayer,
  out: PsdLayerRecord[],
  pixelMap: Map<number, Uint8Array>,
  nextIndex: () => number,
): void {
  const w = layer.pixels.width;
  const h = layer.pixels.height;
  const idx = nextIndex();

  const channels: PsdChannelInfo[] = [
    { id: 0, length: 0 },
    { id: 1, length: 0 },
    { id: 2, length: 0 },
    { id: -1, length: 0 },
  ];

  const additionalInfo: PsdAdditionalLayerInfo[] = [];
  // Unicode layer name (Photoshop 6.0+).
  additionalInfo.push(buildLuniBlock(layer.name));

  // Layer effects
  if (layer.styles) {
    additionalInfo.push(buildLayerEffects(layer.styles));
  }

  // Layer mask.
  let maskData: Uint8Array = new Uint8Array(new ArrayBuffer(0));
  if (layer.mask) {
    maskData = buildMaskData(layer.mask, layer.x, layer.y) as Uint8Array;
    channels.push({ id: -2, length: 0 });
  }

  const flags = buildFlags(layer);

  const record: PsdLayerRecord = {
    top: layer.y,
    left: layer.x,
    bottom: layer.y + h,
    right: layer.x + w,
    channelCount: channels.length,
    channels,
    blendModeKey: blendModeToPsd(layer.blendMode),
    opacity: Math.round(layer.opacity * 255),
    clipping: layer.clippedToBelow ? 1 : 0,
    flags,
    name: layer.name,
    maskData,
    blendingRangesData: buildDefaultBlendingRanges(),
    channelImageOffset: -1,
    additionalInfo,
  };

  out.push(record);
  const px = new Uint8Array(layer.pixels.data.length);
  px.set(layer.pixels.data);
  pixelMap.set(idx, px);
}

function emitTypeLayer(
  layer: TypeLayer,
  out: PsdLayerRecord[],
  pixelMap: Map<number, Uint8Array>,
  nextIndex: () => number,
): void {
  // Type layers have no pixel data in our model; emit a 1×1 transparent block.
  const idx = nextIndex();
  const channels: PsdChannelInfo[] = [
    { id: 0, length: 0 },
    { id: 1, length: 0 },
    { id: 2, length: 0 },
    { id: -1, length: 0 },
  ];

  const additionalInfo: PsdAdditionalLayerInfo[] = [
    buildLuniBlock(layer.name),
    buildTypeDescriptor(layer),
  ];

  // Layer effects
  if (layer.styles) {
    additionalInfo.push(buildLayerEffects(layer.styles));
  }

  const flags = buildFlags(layer) | 0x10; // pixel data irrelevant

  const record: PsdLayerRecord = {
    top: layer.y,
    left: layer.x,
    bottom: layer.y + 1,
    right: layer.x + 1,
    channelCount: channels.length,
    channels,
    blendModeKey: blendModeToPsd(layer.blendMode),
    opacity: Math.round(layer.opacity * 255),
    clipping: layer.clippedToBelow ? 1 : 0,
    flags,
    name: layer.name,
    maskData: new Uint8Array(new ArrayBuffer(0)),
    blendingRangesData: buildDefaultBlendingRanges(),
    channelImageOffset: -1,
    additionalInfo,
  };

  out.push(record);
  pixelMap.set(idx, new Uint8Array([0, 0, 0, 0]));
}

function emitAdjustmentLayer(
  layer: AdjustmentLayer,
  out: PsdLayerRecord[],
  pixelMap: Map<number, Uint8Array>,
  nextIndex: () => number,
): void {
  const idx = nextIndex();
  const channels: PsdChannelInfo[] = [
    { id: 0, length: 0 },
    { id: 1, length: 0 },
    { id: 2, length: 0 },
    { id: -1, length: 0 },
  ];

  const additionalInfo: PsdAdditionalLayerInfo[] = [
    buildLuniBlock(layer.name),
    buildAdjustmentDescriptor(layer),
  ];

  // Layer effects
  if (layer.styles) {
    additionalInfo.push(buildLayerEffects(layer.styles));
  }

  let maskData: Uint8Array = new Uint8Array(new ArrayBuffer(0));
  if (layer.mask) {
    maskData = buildMaskData(layer.mask, layer.x, layer.y) as Uint8Array;
    channels.push({ id: -2, length: 0 });
  }

  const flags = buildFlags(layer) | 0x10; // pixel data irrelevant

  const record: PsdLayerRecord = {
    top: 0,
    left: 0,
    bottom: 1,
    right: 1,
    channelCount: channels.length,
    channels,
    blendModeKey: blendModeToPsd(layer.blendMode),
    opacity: Math.round(layer.opacity * 255),
    clipping: layer.clippedToBelow ? 1 : 0,
    flags,
    name: layer.name,
    maskData,
    blendingRangesData: buildDefaultBlendingRanges(),
    channelImageOffset: -1,
    additionalInfo,
  };

  out.push(record);
  pixelMap.set(idx, new Uint8Array([0, 0, 0, 0]));
}

function emitSmartObjectLayer(
  layer: SmartObjectLayer,
  out: PsdLayerRecord[],
  pixelMap: Map<number, Uint8Array>,
  nextIndex: () => number,
): void {
  const idx = nextIndex();
  // Render Smart Object composite for pixel data
  const composite = compositeSmartObject(layer);
  const w = composite.width;
  const h = composite.height;

  const channels: PsdChannelInfo[] = [
    { id: 0, length: 0 },
    { id: 1, length: 0 },
    { id: 2, length: 0 },
    { id: -1, length: 0 },
  ];

  const additionalInfo: PsdAdditionalLayerInfo[] = [
    buildLuniBlock(layer.name),
    buildSmartObjectDescriptor(layer),
  ];

  // Layer effects
  if (layer.styles) {
    additionalInfo.push(buildLayerEffects(layer.styles));
  }

  const flags = buildFlags(layer);

  const record: PsdLayerRecord = {
    top: layer.y,
    left: layer.x,
    bottom: layer.y + h,
    right: layer.x + w,
    channelCount: channels.length,
    channels,
    blendModeKey: blendModeToPsd(layer.blendMode),
    opacity: Math.round(layer.opacity * 255),
    clipping: layer.clippedToBelow ? 1 : 0,
    flags,
    name: layer.name,
    maskData: new Uint8Array(new ArrayBuffer(0)),
    blendingRangesData: buildDefaultBlendingRanges(),
    channelImageOffset: -1,
    additionalInfo,
  };

  out.push(record);
  pixelMap.set(idx, new Uint8Array(composite.data));
}

/**
 * Background layer — identical wire format to a raster layer, but Photoshop
 * also expects it to land at the bottom of the layer list with no
 * transparency (alpha channel still required by the file format).  We emit
 * the same record shape so readers see it as a normal raster.
 */
function emitBackgroundLayer(
  layer: BackgroundLayer,
  out: PsdLayerRecord[],
  pixelMap: Map<number, Uint8Array>,
  nextIndex: () => number,
): void {
  const w = layer.pixels.width;
  const h = layer.pixels.height;
  const idx = nextIndex();

  const channels: PsdChannelInfo[] = [
    { id: 0, length: 0 },
    { id: 1, length: 0 },
    { id: 2, length: 0 },
    { id: -1, length: 0 },
  ];

  const additionalInfo: PsdAdditionalLayerInfo[] = [
    buildLuniBlock(layer.name),
  ];

  const flags = buildFlags(layer);

  const record: PsdLayerRecord = {
    top: layer.y,
    left: layer.x,
    bottom: layer.y + h,
    right: layer.x + w,
    channelCount: channels.length,
    channels,
    blendModeKey: blendModeToPsd(layer.blendMode),
    opacity: Math.round(layer.opacity * 255),
    clipping: layer.clippedToBelow ? 1 : 0,
    flags,
    name: layer.name,
    maskData: new Uint8Array(0),
    blendingRangesData: buildDefaultBlendingRanges(),
    channelImageOffset: -1,
    additionalInfo,
  };

  out.push(record);
  // Force background alpha to 255 — Photoshop never stores transparent
  // pixels in the locked Background layer and some readers (CS6, libpsd)
  // will glitch the merged composite if the background carries alpha < 255.
  const px = new Uint8Array(layer.pixels.data.length);
  px.set(layer.pixels.data);
  for (let i = 3; i < px.length; i += 4) px[i] = 255;
  pixelMap.set(idx, px);
}

/**
 * Vector / fill layers (Shape, Solid Fill, Gradient Fill, Pattern Fill).
 * We rasterize the layer to a doc-sized canvas and emit it as a flat raster.
 * The original parametric data is lost — a full SoCo / GdFl / VmsK encoder
 * is deferred to follow-up work tracked under #32.
 */
function emitRasterizedLayer(
  layer: ShapeLayer | SolidFillLayer | GradientFillLayer | PatternFillLayer,
  out: PsdLayerRecord[],
  pixelMap: Map<number, Uint8Array>,
  nextIndex: () => number,
  docWidth: number,
  docHeight: number,
): void {
  const idx = nextIndex();
  const rgba = rasterizeLayerToCanvas(layer, docWidth, docHeight);

  const channels: PsdChannelInfo[] = [
    { id: 0, length: 0 },
    { id: 1, length: 0 },
    { id: 2, length: 0 },
    { id: -1, length: 0 },
  ];

  const additionalInfo: PsdAdditionalLayerInfo[] = [
    buildLuniBlock(layer.name),
  ];
  if (layer.styles) {
    additionalInfo.push(buildLayerEffects(layer.styles));
  }

  const flags = buildFlags(layer);

  const record: PsdLayerRecord = {
    top: 0,
    left: 0,
    bottom: docHeight,
    right: docWidth,
    channelCount: channels.length,
    channels,
    blendModeKey: blendModeToPsd(layer.blendMode),
    opacity: Math.round(layer.opacity * 255),
    clipping: layer.clippedToBelow ? 1 : 0,
    flags,
    name: layer.name,
    maskData: new Uint8Array(0),
    blendingRangesData: buildDefaultBlendingRanges(),
    channelImageOffset: -1,
    additionalInfo,
  };

  out.push(record);
  pixelMap.set(idx, rgba);
}

/**
 * Rasterize a fill / shape layer to a doc-sized RGBA8 buffer.  This is the
 * fallback path for layer kinds that PSD encodes through descriptors we
 * don't (yet) write — see #32 follow-up work.
 *
 * Implementation note: DOM-free.  The renderer in `src/core/render/` would
 * give a more accurate result but it pulls in CanvasKit / OffscreenCanvas
 * and is forbidden inside `src/core/io/`.  We do enough math by hand to
 * preserve the appearance for the common cases (Solid Fill = constant
 * color, Gradient Fill linear = lerp, Pattern Fill = tile copy, Shape =
 * approximated bounding rect fill).
 */
function rasterizeLayerToCanvas(
  layer: ShapeLayer | SolidFillLayer | GradientFillLayer | PatternFillLayer,
  docWidth: number,
  docHeight: number,
): Uint8Array {
  const data = new Uint8Array(docWidth * docHeight * 4);

  if (layer.kind === "solid-fill") {
    const c = layer.color;
    const a = Math.round(c.a * 255);
    for (let i = 0; i < docWidth * docHeight; i++) {
      data[i * 4 + 0] = c.r & 0xff;
      data[i * 4 + 1] = c.g & 0xff;
      data[i * 4 + 2] = c.b & 0xff;
      data[i * 4 + 3] = a & 0xff;
    }
    return data;
  }

  if (layer.kind === "gradient-fill") {
    // Linear gradient between first and last stop.  Higher-order interpolation
    // (angle, radial, pattern of midpoints) is deferred.
    const stops = layer.stops.length > 0
      ? layer.stops
      : [
          { position: 0, color: { r: 0, g: 0, b: 0, a: 1 } },
          { position: 1, color: { r: 255, g: 255, b: 255, a: 1 } },
        ];
    const rad = ((layer.angle ?? 90) - 90) * Math.PI / 180;
    const ux = Math.cos(rad);
    const uy = Math.sin(rad);
    // Project (x, y) onto the gradient axis: t = ((x - cx) ux + (y - cy) uy)
    // normalized to roughly [0, 1] across the doc.
    const cx = docWidth / 2;
    const cy = docHeight / 2;
    const halfLen = Math.max(1, Math.abs(ux) * docWidth / 2 + Math.abs(uy) * docHeight / 2);
    for (let y = 0; y < docHeight; y++) {
      for (let x = 0; x < docWidth; x++) {
        const t = ((x - cx) * ux + (y - cy) * uy) / (2 * halfLen) + 0.5;
        const c = sampleGradient(stops, t);
        const i = (y * docWidth + x) * 4;
        data[i + 0] = c.r;
        data[i + 1] = c.g;
        data[i + 2] = c.b;
        data[i + 3] = Math.round(c.a * 255);
      }
    }
    return data;
  }

  if (layer.kind === "pattern-fill") {
    if (!layer.pattern || layer.pattern.width <= 0 || layer.pattern.height <= 0) {
      return data; // transparent
    }
    const src = layer.pattern.pixels;
    const pw = layer.pattern.width;
    const ph = layer.pattern.height;
    for (let y = 0; y < docHeight; y++) {
      const sy = y % ph;
      for (let x = 0; x < docWidth; x++) {
        const sx = x % pw;
        const si = (sy * pw + sx) * 4;
        const di = (y * docWidth + x) * 4;
        data[di + 0] = src[si + 0] ?? 0;
        data[di + 1] = src[si + 1] ?? 0;
        data[di + 2] = src[si + 2] ?? 0;
        data[di + 3] = src[si + 3] ?? 0;
      }
    }
    return data;
  }

  if (layer.kind === "shape") {
    // Approximate: fill the path's bounding box with the fill color.  A real
    // shape rasterizer lives in src/core/render/, not here — see #32.
    if (!layer.fillColor) return data;
    const c = layer.fillColor;
    const a = Math.round(c.a * 255);
    // Path bounds: walk the path's segments and compute min/max.
    const bounds = computePathBounds(layer);
    const x0 = Math.max(0, Math.floor(bounds.x0));
    const y0 = Math.max(0, Math.floor(bounds.y0));
    const x1 = Math.min(docWidth, Math.ceil(bounds.x1));
    const y1 = Math.min(docHeight, Math.ceil(bounds.y1));
    for (let y = y0; y < y1; y++) {
      for (let x = x0; x < x1; x++) {
        const i = (y * docWidth + x) * 4;
        data[i + 0] = c.r & 0xff;
        data[i + 1] = c.g & 0xff;
        data[i + 2] = c.b & 0xff;
        data[i + 3] = a & 0xff;
      }
    }
    return data;
  }

  return data;
}

function sampleGradient(
  stops: ReadonlyArray<{ position: number; color: { r: number; g: number; b: number; a: number } }>,
  t: number,
): { r: number; g: number; b: number; a: number } {
  const ct = Math.max(0, Math.min(1, t));
  // Find surrounding stops.
  let lo = stops[0]!;
  let hi = stops[stops.length - 1]!;
  for (let i = 0; i < stops.length - 1; i++) {
    if (ct >= stops[i]!.position && ct <= stops[i + 1]!.position) {
      lo = stops[i]!;
      hi = stops[i + 1]!;
      break;
    }
  }
  const span = hi.position - lo.position || 1;
  const f = (ct - lo.position) / span;
  return {
    r: Math.round(lo.color.r + (hi.color.r - lo.color.r) * f),
    g: Math.round(lo.color.g + (hi.color.g - lo.color.g) * f),
    b: Math.round(lo.color.b + (hi.color.b - lo.color.b) * f),
    a: lo.color.a + (hi.color.a - lo.color.a) * f,
  };
}

function computePathBounds(layer: ShapeLayer): { x0: number; y0: number; x1: number; y1: number } {
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  const path = layer.path;
  // PathModel structure unknown to us at this layer — defensively read by
  // duck-typing.  Fall back to the layer's x/y if no segments found.
  const subpaths = (path as { subpaths?: ReadonlyArray<{ points?: ReadonlyArray<{ x: number; y: number }> }> })?.subpaths;
  if (subpaths) {
    for (const sp of subpaths) {
      if (!sp.points) continue;
      for (const p of sp.points) {
        if (p.x < x0) x0 = p.x;
        if (p.y < y0) y0 = p.y;
        if (p.x > x1) x1 = p.x;
        if (p.y > y1) y1 = p.y;
      }
    }
  }
  if (!Number.isFinite(x0)) {
    // Empty path — emit a 1×1 dot at the layer origin so the layer still
    // shows up in the merged composite.
    x0 = layer.x;
    y0 = layer.y;
    x1 = layer.x + 1;
    y1 = layer.y + 1;
  } else {
    x0 += layer.x;
    y0 += layer.y;
    x1 += layer.x;
    y1 += layer.y;
  }
  return { x0, y0, x1, y1 };
}

// -- Section divider (lsct) -------------------------------------------------

function buildSectionDivider(
  name: string,
  type: 1 | 2 | 3,
  base: { visible: boolean; opacity: number; blendMode: BlendMode | string },
): PsdLayerRecord {
  const channels: PsdChannelInfo[] = [
    { id: 0, length: 0 },
    { id: 1, length: 0 },
    { id: 2, length: 0 },
    { id: -1, length: 0 },
  ];

  const lsctDataBuf = new ArrayBuffer(4);
  const lsctData = new Uint8Array(lsctDataBuf);
  lsctData[3] = type;
  const additionalInfo: PsdAdditionalLayerInfo[] = [
    { signature: "8BIM", key: "lsct", data: lsctData },
  ];
  if (name) {
    additionalInfo.push(buildLuniBlock(name));
  }

  const flags = 0x10 | 0x08; // pixel data irrelevant + Photoshop 5.0+

  return {
    top: 0,
    left: 0,
    bottom: 1,
    right: 1,
    channelCount: channels.length,
    channels,
    blendModeKey: type === 3 ? "pass" : blendModeToPsd(base.blendMode),
    opacity: type === 3 ? 255 : Math.round(base.opacity * 255),
    clipping: 0,
    flags,
    name: type === 3 ? "" : name || "<Layer group>",
    maskData: new Uint8Array(new ArrayBuffer(0)),
    blendingRangesData: new Uint8Array(new ArrayBuffer(0)),
    channelImageOffset: -1,
    additionalInfo,
  };
}

// -- Helper blocks ----------------------------------------------------------

function buildLuniBlock(name: string): PsdAdditionalLayerInfo {
  // Unicode name: UTF-16BE string data.
  const buf = new ArrayBuffer(name.length * 2);
  const data = new Uint8Array(buf);
  for (let i = 0; i < name.length; i++) {
    const code = name.charCodeAt(i);
    data[i * 2] = (code >> 8) & 0xff;
    data[i * 2 + 1] = code & 0xff;
  }
  return { signature: "8BIM", key: "luni", data };
}

/** Build an OSType descriptor for a Smart Object layer (soLd). */
function buildSmartObjectDescriptor(layer: SmartObjectLayer): PsdAdditionalLayerInfo {
  // Minimal soLd descriptor: classID "soLD" with key "Idnt" (identifier)
  // and "fsId" (file reference ID). For embedded Smart Objects we use
  // a local identifier.
  const items = buildDescriptorItems([
    { key: "Idnt", kind: "string", value: layer.id },
    { key: "fsId", kind: "integer", value: parseInt(layer.id.replace(/\D/g, "") || "0") || 1 },
    { key: "null", kind: "boolean", value: true }, // embedded (not linked)
  ]);
  return { signature: "8BIM", key: "soLd", data: items };
}

/** Build an OSType descriptor for an Adjustment layer (AdjL). */
function buildAdjustmentDescriptor(layer: AdjustmentLayer): PsdAdditionalLayerInfo {
  // AdjL descriptor with adjustment kind and parameters
  const kindMap: Record<string, string> = {
    "brightness-contrast": "brightnessContrast",
    "levels": "levels",
    "curves": "curves",
    "hue-saturation": "hueSaturation",
    "color-balance": "colorBalance",
    "black-white": "blackAndWhite",
    "photo-filter": "photoFilter",
    "gradient-map": "gradientMap",
    "invert": "invert",
    "posterize": "posterize",
    "threshold": "threshold",
    "solid-color": "solidColorFill",
    "exposure": "exposure",
    "vibrance": "vibrance",
    "channel-mixer": "channelMixer",
    "color-lookup": "colorLookup",
    "selective-color": "selectiveColor",
  };

  const items: Array<{ key: string; kind: string; value: unknown }> = [
    { key: "Type", kind: "string", value: kindMap[layer.adjustment] || layer.adjustment },
  ];

  // Add parameters
  for (const [k, v] of Object.entries(layer.params)) {
    const itemKind = typeof v === "boolean" ? "boolean"
      : typeof v === "number" ? "double"
      : "string";
    items.push({ key: k, kind: itemKind, value: v });
  }

  const data = buildDescriptorItems(items);
  return { signature: "8BIM", key: "AdjL", data };
}

/** Build an OSType descriptor for a Type layer (TySh). */
function buildTypeDescriptor(layer: TypeLayer): PsdAdditionalLayerInfo {
  const data = buildDescriptorItems([
    { key: "Txt ", kind: "string", value: layer.text },
    { key: "font", kind: "string", value: layer.fontFamily },
    { key: "Sz  ", kind: "double", value: layer.fontSize },
    { key: "fontPostScriptName", kind: "string", value: layer.fontFamily },
    { key: "fontScriptName", kind: "string", value: "Roman" },
    { key: "fontTechnique", kind: "integer", value: 0 },
    { key: "Wdth", kind: "double", value: 100 },
    { key: "Trck", kind: "double", value: layer.tracking },
    { key: "AutoLding", kind: "boolean", value: true },
    { key: "Ldng", kind: "double", value: layer.leading > 0 ? layer.leading * layer.fontSize : layer.fontSize * 1.2 },
    { key: "VScale", kind: "double", value: 100 },
    { key: "HScale", kind: "double", value: 100 },
    { key: "Baseline", kind: "double", value: layer.baselineShift ?? 0 },
    { key: "baseline", kind: "enumerated", value: layer.textBaseline === "top" ? "Top" : layer.textBaseline === "middle" ? "Middle" : "Bottom", typeId: "baseline" },
    { key: "bounds", kind: "list", value: buildRectList(layer.x, layer.y, layer.x + layer.text.length * layer.fontSize * 0.6, layer.y + layer.fontSize * 2) },
    { key: "writingDirection", kind: "enumerated", value: "LeftToRight", typeId: "writingDirection" },
    { key: "underLine", kind: "enumerated", value: layer.underline ? "underline" : "none", typeId: "underline" },
    { key: "strikeOut", kind: "boolean", value: layer.strikethrough },
    { key: "FAnt", kind: "boolean", value: layer.antiAlias !== "none" },
    { key: "FauxBold", kind: "boolean", value: layer.fauxBold },
    { key: "FauxItalic", kind: "boolean", value: layer.fauxItalic },
    { key: "AutoKern", kind: "boolean", value: true },
    { key: "NoRd", kind: "boolean", value: false },
    { key: "textGridding", kind: "enumerated", value: "None", typeId: "textGridding" },
    { key: "Ornt", kind: "enumerated", value: "Horizontal", typeId: "orientation" },
    { key: "textIndex", kind: "integer", value: 0 },
    { key: "textShape", kind: "list", value: buildTextShapeList() },
    { key: "clfa", kind: "list", value: buildColorList(layer.color) },
  ]);

  return { signature: "8BIM", key: "TySh", data };
}

function buildDescriptorItems(items: Array<{ key: string; kind: string; value: unknown; typeId?: string }>): Uint8Array {
  // Simplified OSType descriptor encoding
  // Version (4) + classID (4 null + 4 length + classID string) + name (4 null + 4 length) + item count (4)
  let size = 4 + 8 + 8 + 4; // version + classID + name + count
  for (const item of items) {
    size += 4 + 4; // key (4 chars) + OSType (4 chars)
    switch (item.kind) {
      case "string": size += 4 + (item.value as string).length * 2; break;
      case "double": size += 8; break;
      case "integer": size += 4; break;
      case "boolean": size += 1; break;
      case "enumerated": size += 4 + 4 + 4 + (item.value as string).length + 4 + ((item.typeId || "") as string).length; break;
      case "list": size += 4 + (item.value as Uint8Array).byteLength; break;
    }
  }

  const buf = new ArrayBuffer(size);
  const view = new DataView(buf);
  const data = new Uint8Array(buf);
  let pos = 0;

  // Version
  view.setUint32(pos, 16, false); pos += 4;
  // ClassID (null)
  view.setUint32(pos, 0, false); pos += 4;
  view.setUint32(pos, 0, false); pos += 4;
  // Name (null)
  view.setUint32(pos, 0, false); pos += 4;
  view.setUint32(pos, 0, false); pos += 4;
  // Item count
  view.setUint32(pos, items.length, false); pos += 4;

  for (const item of items) {
    // Key (4 chars)
    for (let i = 0; i < 4; i++) {
      data[pos++] = item.key.charCodeAt(i) || 0x20;
    }
    // OSType
    switch (item.kind) {
      case "string": {
        data[pos++] = 0x74; data[pos++] = 0x65; data[pos++] = 0x78; data[pos++] = 0x74; // "TEXT"
        const str = item.value as string;
        view.setUint32(pos, str.length * 2, false); pos += 4;
        for (let i = 0; i < str.length; i++) {
          view.setUint16(pos, str.charCodeAt(i), false); pos += 2;
        }
        break;
      }
      case "double": {
        data[pos++] = 0x64; data[pos++] = 0x6f; data[pos++] = 0x75; data[pos++] = 0x62; // "doub"
        view.setFloat64(pos, item.value as number, false); pos += 8;
        break;
      }
      case "integer": {
        data[pos++] = 0x6c; data[pos++] = 0x6f; data[pos++] = 0x6e; data[pos++] = 0x67; // "long"
        view.setInt32(pos, item.value as number, false); pos += 4;
        break;
      }
      case "boolean": {
        data[pos++] = 0x62; data[pos++] = 0x6f; data[pos++] = 0x6f; data[pos++] = 0x6c; // "bool"
        data[pos++] = (item.value as boolean) ? 1 : 0; pos += 1;
        break;
      }
      case "enumerated": {
        data[pos++] = 0x65; data[pos++] = 0x6e; data[pos++] = 0x75; data[pos++] = 0x6d; // "enum"
        const typeId = item.typeId || "";
        view.setUint32(pos, 0, false); pos += 4; // null classID
        view.setUint32(pos, typeId.length, false); pos += 4;
        for (let i = 0; i < typeId.length; i++) data[pos++] = typeId.charCodeAt(i);
        const val = item.value as string;
        view.setUint32(pos, 0, false); pos += 4; // null typeID
        view.setUint32(pos, val.length, false); pos += 4;
        for (let i = 0; i < val.length; i++) data[pos++] = val.charCodeAt(i);
        break;
      }
      case "list": {
        data[pos++] = 0x56; data[pos++] = 0x6c; data[pos++] = 0x73; data[pos++] = 0x74; // "VlLs"
        const listData = item.value as Uint8Array;
        view.setUint32(pos, listData.byteLength, false); pos += 4;
        data.set(listData, pos); pos += listData.byteLength;
        break;
      }
    }
  }

  return data;
}

function buildRectList(x0: number, y0: number, x1: number, y1: number): Uint8Array {
  const items = buildDescriptorItems([
    { key: "Left", kind: "double", value: x0 },
    { key: "Top ", kind: "double", value: y0 },
    { key: "Rght", kind: "double", value: x1 },
    { key: "Btom", kind: "double", value: y1 },
  ]);
  return items;
}

function buildColorList(color: { r: number; g: number; b: number; a: number }): Uint8Array {
  const items = buildDescriptorItems([
    { key: "Rd  ", kind: "double", value: color.r / 255 },
    { key: "Grn ", kind: "double", value: color.g / 255 },
    { key: "Bl  ", kind: "double", value: color.b / 255 },
  ]);
  return items;
}

function buildTextShapeList(): Uint8Array {
  const items = buildDescriptorItems([
    { key: "engineData", kind: "string", value: "" },
  ]);
  return items;
}

/** Build lfx2 descriptor for layer effects. */
function buildLayerEffects(styles: LayerStyles): PsdAdditionalLayerInfo {
  type DescItem = { key: string; kind: string; value: unknown; typeId?: string };
  const items: DescItem[] = [];

  if (styles.globalAngle !== undefined) {
    items.push({ key: "gagl", kind: "double", value: styles.globalAngle });
  }
  if (styles.scale !== undefined) {
    items.push({ key: "Scl ", kind: "double", value: styles.scale * 100 });
  }

  if (styles.dropShadow) {
    const ds = styles.dropShadow;
    items.push({ key: "DrSh", kind: "list", value: buildEffectDescriptor([
      { key: "enab", kind: "boolean", value: ds.enabled },
      { key: "Md  ", kind: "enumerated", value: blendModeEnum(ds.blendMode), typeId: "BlnM" },
      { key: "Clr ", kind: "list", value: buildColorDescriptor(ds.color) },
      { key: "Opct", kind: "double", value: ds.opacity * 100 },
      { key: "uglg", kind: "boolean", value: ds.useGlobalLight },
      { key: "lagl", kind: "double", value: ds.angle },
      { key: "Dstn", kind: "double", value: ds.distance },
      { key: "Ckmt", kind: "double", value: ds.spread * 100 },
      { key: "blur", kind: "double", value: ds.size },
      { key: "AntA", kind: "boolean", value: ds.antiAliased },
      { key: "layerConceals", kind: "boolean", value: ds.layerKnocksOut },
      { key: "Nose", kind: "double", value: ds.noise * 100 },
      { key: "TrnS", kind: "list", value: buildContourDescriptor(ds.contour) },
    ]) });
  }

  if (styles.innerShadow) {
    const is_ = styles.innerShadow;
    items.push({ key: "IrSh", kind: "list", value: buildEffectDescriptor([
      { key: "enab", kind: "boolean", value: is_.enabled },
      { key: "Md  ", kind: "enumerated", value: blendModeEnum(is_.blendMode), typeId: "BlnM" },
      { key: "Clr ", kind: "list", value: buildColorDescriptor(is_.color) },
      { key: "Opct", kind: "double", value: is_.opacity * 100 },
      { key: "uglg", kind: "boolean", value: is_.useGlobalLight },
      { key: "lagl", kind: "double", value: is_.angle },
      { key: "Dstn", kind: "double", value: is_.distance },
      { key: "Ckmt", kind: "double", value: is_.choke * 100 },
      { key: "blur", kind: "double", value: is_.size },
      { key: "AntA", kind: "boolean", value: is_.antiAliased },
      { key: "Nose", kind: "double", value: is_.noise * 100 },
      { key: "TrnS", kind: "list", value: buildContourDescriptor(is_.contour) },
    ]) });
  }

  if (styles.outerGlow) {
    const og = styles.outerGlow;
    items.push({ key: "OrGl", kind: "list", value: buildEffectDescriptor([
      { key: "enab", kind: "boolean", value: og.enabled },
      { key: "Md  ", kind: "enumerated", value: blendModeEnum(og.blendMode), typeId: "BlnM" },
      { key: "Opct", kind: "double", value: og.opacity * 100 },
      { key: "Nose", kind: "double", value: og.noise * 100 },
      { key: "Clr ", kind: "list", value: og.fill.kind === "color" ? buildColorDescriptor(og.fill.color) : new Uint8Array(0) },
      { key: "blur", kind: "double", value: og.size },
      { key: "TrnS", kind: "list", value: buildContourDescriptor(og.contour) },
    ]) });
  }

  if (styles.colorOverlay) {
    const co = styles.colorOverlay;
    items.push({ key: "SoFi", kind: "list", value: buildEffectDescriptor([
      { key: "enab", kind: "boolean", value: co.enabled },
      { key: "Md  ", kind: "enumerated", value: blendModeEnum(co.blendMode), typeId: "BlnM" },
      { key: "Clr ", kind: "list", value: buildColorDescriptor(co.color) },
      { key: "Opct", kind: "double", value: co.opacity * 100 },
    ]) });
  }

  if (styles.stroke) {
    const st = styles.stroke;
    items.push({ key: "FrFX", kind: "list", value: buildEffectDescriptor([
      { key: "enab", kind: "boolean", value: st.enabled },
      { key: "Sz  ", kind: "double", value: st.size },
      { key: "Styl", kind: "enumerated", value: strokePositionEnum(st.position), typeId: "FStl" },
      { key: "Md  ", kind: "enumerated", value: blendModeEnum(st.blendMode), typeId: "BlnM" },
      { key: "Opct", kind: "double", value: st.opacity * 100 },
      { key: "PntT", kind: "enumerated", value: strokeFillEnum(st.fillType), typeId: "FrFl" },
      ...(st.color ? [{ key: "Clr ", kind: "list", value: buildColorDescriptor(st.color) }] : []),
    ]) });
  }

  const data = items.length > 0 ? buildDescriptorItems(items) : new Uint8Array(0);
  return { signature: "8BIM", key: "lfx2", data };
}

function buildEffectDescriptor(items: Array<{ key: string; kind: string; value: unknown; typeId?: string }>): Uint8Array {
  return buildDescriptorItems(items);
}

function buildColorDescriptor(color: { r: number; g: number; b: number; a: number }): Uint8Array {
  return buildDescriptorItems([
    { key: "Rd  ", kind: "double", value: color.r },
    { key: "Grn ", kind: "double", value: color.g },
    { key: "Bl  ", kind: "double", value: color.b },
  ]);
}

function buildContourDescriptor(contour: { points: ReadonlyArray<{ x: number; y: number }>; presetName?: string }): Uint8Array {
  const points = contour.points.map((p, i) => ({
    key: "Pnt ", kind: "list" as const, value: buildDescriptorItems([
      { key: "Hrzn", kind: "double", value: p.x * 255 },
      { key: "Vrtc", kind: "double", value: p.y * 255 },
    ]),
  }));
  return buildDescriptorItems([
    ...(contour.presetName ? [{ key: "Nm  ", kind: "string", value: contour.presetName }] : []),
    { key: "Crv ", kind: "list", value: buildDescriptorItems(points) },
  ]);
}

function blendModeEnum(mode: string): string {
  const map: Record<string, string> = {
    normal: "Nrml", dissolve: "Dslv", darken: "Drkn", multiply: "Mltp",
    "color-burn": "CBrn", "linear-burn": "linearBurn", "darker-color": "darkerColor",
    lighten: "Lghn", screen: "Scrn", "color-dodge": "CDdg", "linear-dodge": "linearDodge",
    "lighter-color": "lighterColor", overlay: "Ovrl", "soft-light": "SftL",
    "hard-light": "HrdL", "vivid-light": "vividLight", "linear-light": "linearLight",
    "pin-light": "pinLight", "hard-mix": "hardMix", difference: "Dfrn",
    exclusion: "Xclu", subtract: "blendSubtraction", divide: "blendDivide",
    hue: "H   ", saturation: "Strt", color: "Clr ", luminosity: "Lmns",
  };
  return map[mode] || "Nrml";
}

function strokePositionEnum(pos: string): string {
  const map: Record<string, string> = { outside: "OutF", inside: "InsF", center: "CtrF" };
  return map[pos] || "OutF";
}

function strokeFillEnum(type: string): string {
  const map: Record<string, string> = { color: "SClr", gradient: "GrFl", pattern: "PtrF" };
  return map[type] || "SClr";
}

function buildFlags(layer: {
  lockTransparency: boolean;
  visible: boolean;
}): number {
  let flags = 0;
  if (layer.lockTransparency) flags |= 0x01;
  if (!layer.visible) flags |= 0x02;
  flags |= 0x08; // Photoshop 5.0+ (useful info present)
  return flags;
}

function buildMaskData(
  mask: { pixels: PixelBuffer; enabled: boolean; linked: boolean; x: number; y: number; density: number; feather: number },
  layerX: number,
  layerY: number,
): Uint8Array {
  const w = mask.pixels.width;
  const h = mask.pixels.height;
  if (w <= 0 || h <= 0) {
    const buf = new ArrayBuffer(0);
    return new Uint8Array(buf);
  }

  // Mask data block format:
  // 4× i32: rect (top, left, bottom, right)
  // 1× u8: default color
  // 1× u8: flags
  // 1× u8: real user mask background color
  // Then h×w bytes of mask data (1 byte per pixel)
  const size = 16 + 3 + w * h;
  const buf = new ArrayBuffer(size);
  const data = new Uint8Array(buf);
  const view = new DataView(buf);

  const maskTop = mask.y;
  const maskLeft = mask.x;
  const maskBottom = mask.y + h;
  const maskRight = mask.x + w;

  view.setInt32(0, maskTop, false);
  view.setInt32(4, maskLeft, false);
  view.setInt32(8, maskBottom, false);
  view.setInt32(12, maskRight, false);
  data[16] = 0; // default color (0 = black, 255 = white)
  data[17] = mask.enabled ? 0 : 2; // flags: bit 1 = disabled
  data[18] = 0; // real user mask bg color

  // Copy mask alpha values
  const src = mask.pixels.data;
  let pos = 19;
  for (let i = 0; i < w * h; i++) {
    data[pos++] = src[i * 4 + 3] ?? 0;
  }

  return data;
}

// -- Merged composite -------------------------------------------------------

function compositeDocument(doc: Document): Uint8Array {
  const w = doc.width;
  const h = doc.height;
  const result = new Uint8Array(w * h * 4);

  for (const layer of doc.layers) {
    compositeLayerToBuffer(result, w, h, layer);
  }

  return result;
}

function compositeLayerToBuffer(
  dst: Uint8Array,
  docW: number,
  docH: number,
  layer: Layer,
): void {
  if (!layer.visible || layer.opacity <= 0) return;

  if (layer.kind === "group") {
    for (const child of (layer as GroupLayer).children) {
      compositeLayerToBuffer(dst, docW, docH, child);
    }
    return;
  }

  // Composite non-raster kinds via their rasterized form so the merged
  // image data section reflects the user's view.  This is the path that
  // Photoshop / Preview / Finder thumbnails actually display.
  if (layer.kind === "background") {
    blitRgba(dst, docW, docH, (layer as BackgroundLayer).pixels.data,
      (layer as BackgroundLayer).pixels.width, (layer as BackgroundLayer).pixels.height,
      layer.x, layer.y, layer.opacity);
    return;
  }
  if (
    layer.kind === "solid-fill" ||
    layer.kind === "gradient-fill" ||
    layer.kind === "pattern-fill" ||
    layer.kind === "shape"
  ) {
    const buf = rasterizeLayerToCanvas(
      layer as ShapeLayer | SolidFillLayer | GradientFillLayer | PatternFillLayer,
      docW,
      docH,
    );
    blitRgba(dst, docW, docH, buf, docW, docH, 0, 0, layer.opacity);
    return;
  }
  if (layer.kind === "smart-object") {
    const composite = compositeSmartObject(layer as SmartObjectLayer);
    blitRgba(dst, docW, docH, composite.data, composite.width, composite.height,
      layer.x, layer.y, layer.opacity);
    return;
  }

  if (layer.kind !== "raster") return;
  const raster = layer as RasterLayer;
  const src = raster.pixels.data;
  const srcW = raster.pixels.width;
  const srcH = raster.pixels.height;
  if (srcW <= 0 || srcH <= 0) return;

  const lx = raster.x;
  const ly = raster.y;
  const opacity = layer.opacity;

  // Handle mask
  const maskPixels = layer.mask?.pixels;
  const maskEnabled = layer.mask?.enabled ?? false;

  for (let sy = 0; sy < srcH; sy++) {
    const dy = ly + sy;
    if (dy < 0 || dy >= docH) continue;
    for (let sx = 0; sx < srcW; sx++) {
      const dx = lx + sx;
      if (dx < 0 || dx >= docW) continue;

      const si = (sy * srcW + sx) * 4;
      const di = (dy * docW + dx) * 4;

      let srcA = src[si + 3] ?? 0;

      // Apply mask
      if (maskEnabled && maskPixels) {
        const ma = maskPixels.data[(sy * srcW + sx) * 4 + 3] ?? 255;
        srcA = Math.round((srcA * ma) / 255);
      }

      // Apply layer opacity
      srcA = Math.round(srcA * opacity);
      if (srcA <= 0) continue;

      const srcR = src[si] ?? 0;
      const srcG = src[si + 1] ?? 0;
      const srcB = src[si + 2] ?? 0;

      const dstA = dst[di + 3] ?? 0;
      if (dstA === 0) {
        // Destination is transparent — just copy source
        dst[di] = srcR;
        dst[di + 1] = srcG;
        dst[di + 2] = srcB;
        dst[di + 3] = srcA;
      } else {
        // Alpha compositing (source over)
        const outA = srcA + Math.round((dstA * (255 - srcA)) / 255);
        if (outA > 0) {
          dst[di] = Math.round(
            (srcR * srcA + dst[di]! * dstA * (1 - srcA / 255)) / outA,
          );
          dst[di + 1] = Math.round(
            (srcG * srcA + dst[di + 1]! * dstA * (1 - srcA / 255)) / outA,
          );
          dst[di + 2] = Math.round(
            (srcB * srcA + dst[di + 2]! * dstA * (1 - srcA / 255)) / outA,
          );
          dst[di + 3] = outA;
        }
      }
    }
  }
}

/** Composite a Smart Object's embedded document into a pixel buffer. */
function compositeSmartObject(layer: SmartObjectLayer): { data: Uint8Array; width: number; height: number } {
  const doc = layer.embedded;
  const w = doc.width;
  const h = doc.height;
  const result = new Uint8Array(w * h * 4);

  for (const l of doc.layers) {
    compositeLayerToBuffer(result, w, h, l);
  }

  return { data: result, width: w, height: h };
}

/**
 * Blit a source RGBA8 buffer onto the destination, honoring a layer-level
 * opacity multiplier.  Used by the merged-composite path for layer kinds
 * that aren't intrinsically raster (fills, shapes, smart objects).
 */
function blitRgba(
  dst: Uint8Array,
  docW: number,
  docH: number,
  src: ArrayLike<number>,
  srcW: number,
  srcH: number,
  offX: number,
  offY: number,
  opacity: number,
): void {
  for (let sy = 0; sy < srcH; sy++) {
    const dy = offY + sy;
    if (dy < 0 || dy >= docH) continue;
    for (let sx = 0; sx < srcW; sx++) {
      const dx = offX + sx;
      if (dx < 0 || dx >= docW) continue;
      const si = (sy * srcW + sx) * 4;
      const di = (dy * docW + dx) * 4;
      const srcR = src[si + 0] ?? 0;
      const srcG = src[si + 1] ?? 0;
      const srcB = src[si + 2] ?? 0;
      let srcA = Math.round((src[si + 3] ?? 0) * opacity);
      if (srcA <= 0) continue;
      const dstA = dst[di + 3] ?? 0;
      if (dstA === 0) {
        dst[di + 0] = srcR;
        dst[di + 1] = srcG;
        dst[di + 2] = srcB;
        dst[di + 3] = srcA;
      } else {
        const outA = srcA + Math.round((dstA * (255 - srcA)) / 255);
        if (outA > 0) {
          dst[di + 0] = Math.round((srcR * srcA + dst[di + 0]! * dstA * (1 - srcA / 255)) / outA);
          dst[di + 1] = Math.round((srcG * srcA + dst[di + 1]! * dstA * (1 - srcA / 255)) / outA);
          dst[di + 2] = Math.round((srcB * srcA + dst[di + 2]! * dstA * (1 - srcA / 255)) / outA);
          dst[di + 3] = outA;
        }
      }
    }
  }
}
